import type { Edge } from "@xyflow/react";
import type { RFNodeType } from "../components/GraphBuilder/Nodes/utils/types";

export interface NormalizedGraph {
  nodesById: Record<string, RFNodeType>;
  nodeIds: string[];
  edgesById: Record<string, Edge>;
  edgeIds: string[];
}

export function normalizeNodes(nodes: RFNodeType[]): {
  nodesById: Record<string, RFNodeType>;
  nodeIds: string[];
} {
  const nodesById: Record<string, RFNodeType> = {};
  const nodeIds: string[] = [];
  for (const node of nodes) {
    nodesById[node.id] = node;
    nodeIds.push(node.id);
  }
  return { nodesById, nodeIds };
}

export function normalizeEdges(edges: Edge[]): {
  edgesById: Record<string, Edge>;
  edgeIds: string[];
} {
  const edgesById: Record<string, Edge> = {};
  const edgeIds: string[] = [];
  for (const edge of edges) {
    edgesById[edge.id] = edge;
    edgeIds.push(edge.id);
  }
  return { edgesById, edgeIds };
}

export function normalizeGraph(
  nodes: RFNodeType[],
  edges: Edge[],
): NormalizedGraph {
  return {
    ...normalizeNodes(nodes),
    ...normalizeEdges(edges),
  };
}

export function denormalizeNodes(
  nodesById: Record<string, RFNodeType>,
  nodeIds: string[],
): RFNodeType[] {
  return nodeIds.map((id) => nodesById[id]).filter(Boolean);
}

export function denormalizeEdges(
  edgesById: Record<string, Edge>,
  edgeIds: string[],
): Edge[] {
  return edgeIds.map((id) => edgesById[id]).filter(Boolean);
}

export function denormalizeGraph(graph: NormalizedGraph): {
  nodes: RFNodeType[];
  edges: Edge[];
} {
  return {
    nodes: denormalizeNodes(graph.nodesById, graph.nodeIds),
    edges: denormalizeEdges(graph.edgesById, graph.edgeIds),
  };
}

export function getErrorMessage(error: unknown): string {
  return (
    JSON.parse((error as { data?: { detail?: string } })?.data?.detail ?? "{}")
      ?.error?.message ?? null
  );
}

/**
 * Like getErrorMessage but safe for both `detail` shapes the BE returns:
 * a JSON-encoded `{ error: { message } }` string or a plain string. Never
 * throws and only ever returns a string (or null for the generic fallback).
 */
export function getApiErrorMessage(error: unknown): string | null {
  const detail = (error as { data?: { detail?: unknown } })?.data?.detail;
  if (typeof detail !== "string" || detail.length === 0) return null;
  try {
    const message = JSON.parse(detail)?.error?.message;
    return typeof message === "string" ? message : detail;
  } catch {
    return detail;
  }
}
